import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { Row } from "@faire/web/slate/Layout";
import { getSlateIcon } from "@faire/web/slate/SlateMappings/getSlateIcon";
import { NoTranslate } from "@faire/web/ui/lib/localization";
import React, { useMemo } from "react";
import styled from "styled-components";

import { FlagImg } from "@faire/retailer/components/FlagImg";
import { LoadableTypography } from "@faire/retailer/components/GhostLoading/LoadableTypography";
import * as RetailerPaths from "@faire/retailer/paths";

import { ILeanProductTile } from "../MarketplaceProduct/LeanProtos/ILeanProductTile";
import { MarketplaceProductBrandLink } from "../MarketplaceProductBrandLink";

interface IBrandNameTextProps {
  brand?: ILeanProductTile.IBrand;
  isLoaded: boolean;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  onBrandLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const BrandNameText: React.FC<IBrandNameTextProps> = ({
  brand,
  isLoaded,
  generateBrandUrl,
  onBrandLinkClick,
}) => {
  const brandUrl = useMemo(() => {
    if (!brand?.token) {
      return undefined;
    }
    if (!generateBrandUrl) {
      return RetailerPaths.brandPage(brand.token);
    }
    return generateBrandUrl(brand.token);
  }, [brand?.token, generateBrandUrl]);
  const BrandIcon = getSlateIcon(brand?.icon);
  return brand ? (
    <MakerName justify="space-between">
      <MarketplaceProductBrandLink
        token={brand?.token}
        onClick={(ev) => {
          onBrandLinkClick?.(ev);
        }}
        data-test-id="marketplace-product-brand-link"
        to={brandUrl}
      >
        <Row align="center" gap="1/4x">
          {brand.icon ? (
            <BrandIcon
              fontSize="12px"
              color={getSlateColorToken(brand?.icon.color_token)}
            />
          ) : null}
          <LoadableTypography
            isLoaded={isLoaded}
            variant="paragraphSansRegular"
            maxLines={1}
          >
            {brand?.region_flag_image?.url ? (
              <FlagImg
                src={brand.region_flag_image.url}
                alt={brand.region_flag_image.alt_text}
              />
            ) : null}
            <NoTranslate>
              <ResponsiveBrandName brandName={brand.name ?? ""} />
            </NoTranslate>
          </LoadableTypography>
        </Row>
      </MarketplaceProductBrandLink>
    </MakerName>
  ) : null;
};

const Underline = styled.span``;

const MakerName = styled(Row).attrs({
  justify: "space-between",
})`
  overflow: hidden;
  padding-top: 4px;
  text-decoration: underline;
`;

const ResponsiveBrandName = ({ brandName }: { brandName: string }) => {
  return <Underline>{brandName}</Underline>;
};
